import React, { useState, useCallback, useMemo, useEffect } from 'react';
import _ from 'underscore';
import {
  Menu,
  Button,
  MenuButton,
  MenuList,
  MenuDivider,
  MenuPopover,
  MenuTrigger,
  makeStyles,
  tokens,
  Checkbox,
  mergeClasses,
} from '@fluentui/react-components';
import {
  Dismiss20Regular,
} from '@fluentui/react-icons';

const useStyles = makeStyles({
  menuPopover: {
    minWidth: '180px',
  },
  unselectedMenuButton: {
    '&[aria-expanded="false"]': {
      fontWeight: 'unset',
    },
  },
  enabledClearButton: {
    float: 'right',
    color: tokens.colorNeutralForeground2BrandHover,
  },
  disabledClearButton: {
    float: 'right',
    color: tokens.colorNeutralForegroundDisabled,
  },
  checkbox: {
    '& label': {
      paddingLeft: '0px',
      fontWeight: 'unset',
    },
  },
});

export const AssetSourceFilter = (props) => {
  const {
    i18n,
    defaultSelectedAssetSources,
    options,
    onChange,
    forceClear,
    onDismiss,
    defaultOpen = false,
  } = props;

  const classes = useStyles();
  const [selectedAssetSources, setSelectedAssetSources] = useState(defaultSelectedAssetSources);
  const [isOpen, setIsOpen] = useState(defaultOpen);

  const selectedOptions = useMemo(
    () => _.filter(options, ({ key }) => _.contains(selectedAssetSources, key)) || [],
    [selectedAssetSources, options]
  );

  const onChangeCallback = useCallback((key, checked) => {
    const selectedSources = checked ? _.sortBy([...selectedAssetSources, key]) : _.without(selectedAssetSources, key);
    setSelectedAssetSources(selectedSources || []);
  }, [selectedAssetSources]);

  // the string shown on dropdown title
  const dropdownTitle = useMemo(() => {
    if (_.isEmpty(selectedOptions)) return null;
    const title = _.uniq(selectedOptions.map(({ text }) => text)).join('; ');
    return title;
  }, [selectedOptions]);

  const isSelected = useMemo(() => !_.isEmpty(selectedAssetSources), [selectedAssetSources]);

  // when the X button on dropdown title is clicked
  const onDropdownClear = useCallback(() => {
    setSelectedAssetSources([]);
    if (onChange && !_.isEmpty(defaultSelectedAssetSources)) {
      onChange([]);
    }
    if (onDismiss) {
      onDismiss();
    }
  }, [defaultSelectedAssetSources, onChange, onDismiss]);

  // when dropdown list is dismissed
  const onDropdownDismiss = useCallback(() => {
    if (onChange && !_.isEqual(defaultSelectedAssetSources, selectedAssetSources)) {
      onChange(selectedAssetSources, dropdownTitle);
    }
  }, [defaultSelectedAssetSources, dropdownTitle, selectedAssetSources, onChange]);

  const clear = () => {
    setIsOpen(false);
    onDropdownClear();
  };

  const onOpenChange = (e, data) => {
    setIsOpen(data.open);
  };

  useEffect(() => {
    if (forceClear) {
      clear();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceClear]);

  useEffect(() => {
    if (!isOpen) {
      onDropdownDismiss();
    }
  }, [isOpen, onDropdownDismiss]);

  return (
    <Menu open={isOpen} hasCheckmarks onOpenChange={onOpenChange}>
      <MenuTrigger disableButtonEnhancement>
        <MenuButton appearance="subtle" className={isSelected ? 'asset-source-button' : mergeClasses(classes.unselectedMenuButton, 'asset-source-button')}>
          {dropdownTitle || i18n.getString(_TL_('Source'))}
        </MenuButton>
      </MenuTrigger>

      <MenuPopover className={mergeClasses(classes.menuPopover, 'asset-source-menu')}>
        <MenuList>
          <div className="custom-source">
            {options.map(option => (
              <div key={option.key}>
                <Checkbox
                  label={option.title}
                  className={mergeClasses(option.key, classes.checkbox)}
                  checked={_.contains(selectedAssetSources, option.key)}
                  onChange={(evt, data) => onChangeCallback(option.key, data.checked)}
                />
              </div>
            ))}
          </div>
          <MenuDivider />
          <div>
            <Button
              className={isSelected ? classes.enabledClearButton : classes.disabledClearButton}
              disabled={!isSelected}
              appearance="transparent"
              icon={<Dismiss20Regular />}
              onClick={() => clear()}
            >
              {i18n.getString(_TL_('Clear'))}
            </Button>
          </div>
        </MenuList>
      </MenuPopover>
    </Menu>
  );
};

